import {
  PipelineEntity,
  PipelineGraphEdgeEntity,
  PipelineGraphEntity,
  PipelineGraphNodeEntity
} from "@ots-frontend/ots-data";
import styled from "@emotion/styled";
import {useCallback, useMemo, useState} from "react";
import {IEdge, INode, SelectionT} from "react-digraph";
import {useTranslation} from "react-i18next";
import {Card} from "../../card/Card/Card";
import {Divider, Text, Title} from "@mantine/core";
import {PipelineGraphNodeCard} from "../../card/PipelineGraphNodeCard/PipelineGraphNodeCard";
import { PipelineGraphView } from "../PipelineGraphView/PipelineGraphView";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";

export type PipelineGraphViewerProps = {
  pipelineGraph: PipelineGraphEntity
  pipeline?: PipelineEntity
}

export interface PipelineGraphViewerValues {
  displayName: string
  effectiveDate: Date
  nodes: PipelineGraphNodeEntity[]
  edges: PipelineGraphEdgeEntity[]
}

const GraphContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const PanelContainer = styled.div`
  pointer-events: auto;
  border: 0;
  padding: 10px;
  position: absolute;
  top: 0;
  right: 0;
`;

const NodePanelContainer = styled.div`
  pointer-events: auto;
  border: 0;
  padding: 10px;
  position: absolute;
  bottom: 0;
  right: 0;
`;

export const PipelineGraphViewer = (props: PipelineGraphViewerProps) => {
  const {
    pipelineGraph,
    pipeline
  } = props;

  const {t} = useTranslation()

  const [selectedNode, setSelectedNode] = useState<INode | undefined>(undefined);
  const [selectedEdge, setSelectedEdge] = useState<IEdge | undefined>(undefined);
  const [selectedXy, setSelectedXy] = useState<{ x: number; y: number } | undefined>(undefined);

  const onBackgroundClick = useCallback((x: number, y: number, event: any) => {
      setSelectedXy({x, y});
    },
    [setSelectedXy]
  );

  const selectedNodes = useMemo(() => {
    return selectedNode ? new Map<string, INode>([[selectedNode.id, selectedNode]]) : null
  }, [selectedNode])

  const selectedEdges = useMemo(() => {
    return selectedEdge ? new Map<string, IEdge>([[selectedEdge.id, selectedEdge]]) : null
  }, [selectedEdge])

  const onGraphSelect = useCallback((selection: SelectionT) => {
    const selectedNodes = selection.nodes;
    const selectedEdges = selection.edges;

    let nodeSelection: INode | undefined = undefined;
    let edgeSelection: IEdge | undefined = undefined;

    if (selectedNodes) {
      const [selection] = selectedNodes.values();
      if (selection) {
        nodeSelection = {...selection};
        if (selection.x && selection.y) {
          setSelectedXy({
            x: selection.x,
            y: selection.y,
          });
        }
      }
    }

    if (selectedEdges) {
      const [selection] = selectedEdges.values();
      if (selection) {
        edgeSelection = {...selection};
      }
    }

    setSelectedNode(nodeSelection);
    setSelectedEdge(edgeSelection);
  }, []);

  return (
    <GraphContainer>
      <PipelineGraphView
        pipelineGraph={pipelineGraph}
        selected={{
          nodes: selectedNodes,
          edges: selectedEdges,
        }}
        allowMultiselect={false}
        onSelect={onGraphSelect}
        onBackgroundClick={onBackgroundClick}
      />
      <PanelContainer>
        <Card shadow="sm" padding="lg" radius="md" miw={300}>
          <Title order={3}>
            {t("common.entity.pipelinegraph", "Pipeline Graph")}
          </Title>
          <Title order={5}>
            {pipelineGraph.displayableName}
          </Title>
          {pipeline &&
            <Title order={6}>
              {`Pipeline: ${pipeline.name}`}
            </Title>
          }
          <Divider />
          <Text ta="left" fz="sm" weight={500} mt="md">
            {t("common.entity.pipelinegraph.effectivedttm", "Effective")}
          </Text>
          <Text ta="left" c="dimmed" fz="xs">
            <FormattedDateTime date={pipelineGraph.effectiveDttm} />
          </Text>
        </Card>
      </PanelContainer>
      {selectedNode &&
        <NodePanelContainer>
          <PipelineGraphNodeCard
            node={selectedNode}/>
        </NodePanelContainer>
      }
    </GraphContainer>
  );
}
